import axios, { AxiosResponse } from 'axios';
import { GatewayResponse, Page, PageRequest } from '../api.types';
import {
  BrokerModel,
  ClassifierListFilters,
  ClassifierModel,
  ClassifierTypeModel, EditBrokerRequest,
  EditClassifierRequest, EditSupplierBrokerRequest,
  NotificationModel,
  NotificationRequestModel, SupplierBrokerModel,
  SupplierModel
} from './admin.types';

const FRONTEND_API_URL = window.config.apiUrls.frontendServerApi;

const getSupplier = async () => {
  const { data }: AxiosResponse<GatewayResponse<SupplierModel>> = await axios.get(
    `${FRONTEND_API_URL}/suppliers/:supplier`
  );

  return data;
};

const getNotificationsList = async () => {
  const { data }: AxiosResponse<GatewayResponse<Page<NotificationModel>>> = await axios.get(
    `${FRONTEND_API_URL}/admin/notifications`
  );

  return data;
};

const getNotification = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<NotificationModel>> = await axios.get(
    `${FRONTEND_API_URL}/admin/notifications/${id}`
  );

  return data;
};

const addNewNotification = async (request: NotificationRequestModel) => {
  const { data }: AxiosResponse<GatewayResponse<NotificationModel>> = await axios.post(
    `${FRONTEND_API_URL}/admin/notifications`,
    request
  );

  return data;
};

const editNotification = async (request: NotificationRequestModel, id: number) => {
  const { data }: AxiosResponse<GatewayResponse<NotificationModel>> = await axios.put(
    `${FRONTEND_API_URL}/admin/notifications/${id}`,
    request
  );

  return data;
};

const deleteNotification = async (id: number) => {
  await axios.delete(`${FRONTEND_API_URL}/admin/notifications/${id}`);
};

const userHasSeenNotification = async (id: number) => {
  await axios.patch(`${FRONTEND_API_URL}/user/notifications/${id}`, {});
};

const listAllClassifierTypes = async () => {
  const { data }: AxiosResponse<GatewayResponse<Page<ClassifierTypeModel>>> = await axios.get(
    `${FRONTEND_API_URL}/setup/classifiers/type`
  );

  return data;
};

const listAllBrokers = async (page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<BrokerModel>>> = await axios.get(
    `${FRONTEND_API_URL}/setup/brokers`
  );

  return data;
};

const addBroker = async (request: EditBrokerRequest) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerModel>> = await axios.post(
    `${FRONTEND_API_URL}/setup/brokers`,
    request
  );
  return data;
};

const editBroker = async (id: number, request: EditBrokerRequest) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerModel>> = await axios.put(
    `${FRONTEND_API_URL}/setup/brokers/${id}`,
    request
  );
  return data;
};

const getOneBroker = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerModel>> = await axios.get(
    `${FRONTEND_API_URL}/setup/brokers/${id}`
  );

  return data;
};

const deleteBroker = async (id: number) => {
  await axios.delete(`${FRONTEND_API_URL}/setup/brokers/${id}`);
};

const listAllSupplierBrokers = async (page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<BrokerModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/brokers`
  );

  return data;
};

const addSupplierBroker = async (id: number, request: EditSupplierBrokerRequest) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/brokers/${id}`,
    request
  );
  return data;
};


const editSupplierBroker = async (id: number, request: EditSupplierBrokerRequest) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/brokers/${id}`,
    request
  );
  return data;
};

const getOneSupplierBroker = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<SupplierBrokerModel>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/brokers/${id}`
  );

  return data;
};

const deleteSupplierBroker = async (id: number) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/brokers/${id}`);
};

const listAllClassifiers = async (filters?: ClassifierListFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<ClassifierModel>>> = await axios.get(
    `${FRONTEND_API_URL}/setup/classifiers`,
    {
      params: {
        name: filters ? filters.name : undefined,
        code: filters ? filters.code : undefined,
        description: filters ? filters.description : undefined,
        value: filters ? filters.value : undefined,
        classifierTypeCode: filters ? filters.classifierTypeCode : undefined,
        page: page ? page.page : undefined,
        size: page ? page.size : undefined,
        sortField: page ? page.sortField : undefined,
        sortDirection: page ? page.sortDirection : undefined
      }
    }
  );

  return data;
};

const listAllClassifiersByType = async (classifierTypeCode: string, page?: number): Promise<ClassifierModel[]> => {
  const { data }: AxiosResponse<GatewayResponse<Page<ClassifierModel>>> = await axios.get(
    `${FRONTEND_API_URL}/setup/classifiers`,
    {
      params: {
        classifierTypeCode,
        size: 500,
        page: page ?? 0
      }
    }
  );

  const content = data.data.content;
  let response: ClassifierModel[] = [];
  if (!data.data.last) response = await listAllClassifiersByType(classifierTypeCode, page ? page + 1 : 1);

  return content.concat(response);
};

const getOneClassifier = async (code: string) => {
  const { data }: AxiosResponse<GatewayResponse<ClassifierModel>> = await axios.get(
    `${FRONTEND_API_URL}/setup/classifiers/${code}`
  );

  return data;
};

const addNewClassifier = async (
  request: ClassifierModel
) => {
  const { data }: AxiosResponse<GatewayResponse<ClassifierModel>> = await axios.post(
    `${FRONTEND_API_URL}/setup/classifiers`,
    request
  );

  return data;
};

const editClassifier = async (
  code: string,
  request: EditClassifierRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<ClassifierModel>> = await axios.put(
    `${FRONTEND_API_URL}/setup/classifiers/${code}`,
    request
  );

  return data;
};

const deleteClassifier = async (classifierCode: string) => {
  await axios.delete(`${FRONTEND_API_URL}/setup/classifiers/${classifierCode}`);
};

const api = {
  suppliers: {
    getSupplier
  },
  notifications: {
    getNotification,
    deleteNotification,
    getNotificationsList,
    addNewNotification,
    editNotification,
    userHasSeenNotification
  },
  brokers: {
    listAllBrokers,
    getOneBroker,
    addBroker,
    editBroker,
    deleteBroker
  },
  supplierBrokers: {
    listAllSupplierBrokers,
    getOneSupplierBroker,
    addSupplierBroker,
    editSupplierBroker,
    deleteSupplierBroker
  },
  ClassifierTypes: {
    listAllClassifierTypes
  },
  Classifiers: {
    listAllClassifiers,
    getOneClassifier,
    addNewClassifier,
    editClassifier,
    deleteClassifier,
    listAllClassifiersByType
  }

};

export default api;